import { useMemo } from 'react';
import { customizer } from '@sas/design-tokenizer';
import PRODUCT_TOKENS_JSON_NEW from '@sas/new-product-tokens/processed-tokens.json';
import PRODUCT_TOKENS_JSON from '@sas/product-tokens/processed-tokens.json';
import { defaultThemes, deriveNovaTokens } from '../../../design-tokens';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useDesignTokens, useThemeContext } from '../../../theme';

/**
 * Hook that generates table override themes.
 *
 * @param ignoreTableOverrideTheme whether or not to use the base theme without table overrides applied
 * @param header whether or not to calculate the theme using header overrides or not
 */

export function useTableOverrideTheme(ignoreTableOverrideTheme = false, isHeader = false) {
  // Feature flag for new token values
  const featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const productTokens = featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues ? PRODUCT_TOKENS_JSON_NEW : PRODUCT_TOKENS_JSON;
  const dt = useDesignTokens();
  const themeContext = useThemeContext?.();
  const overrideTheme = useMemo(() => {
    // Fallback to the light theme if theme context is undefined
    if (!themeContext) {
      return {
        currentTheme: defaultThemes.light,
        inverseTheme: defaultThemes.light
      };
    }
    if (isHeader && !dt.overrides.table.headerLabelColor || !isHeader && !dt.overrides.table.textColor) {
      return {
        currentTheme: themeContext.currentTheme,
        inverseTheme: themeContext.inverseTheme
      };
    }
    const new_dt = deriveNovaTokens(getTableOverrideTheme(dt, isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor));
    const customizedTokens = customizer(productTokens, {
      'color-neutral': (isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor) ?? dt.tokens['color-neutral'],
      'color-text-neutral': (isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor) ?? dt.tokens['color-text-neutral'],
      'color-body-text': (isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor) ?? dt.tokens['color-body-text'],
      'color-heading-text': isHeader && dt.overrides.table.headerLabelColor ? dt.overrides.table.headerLabelColor : dt.tokens['color-heading-text'],
      'brand-color-grey': (isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor) ?? dt.tokens['color-neutral']
    });
    const tokensExperimental = {};
    (customizedTokens.find(t => t.name === themeContext.currentTheme.baseThemeName)?.tokens ?? []).forEach(({
      name,
      value
    }) => {
      // font-family needs to be locale aware so the overrides are being done in react.ts
      if (name === 'font-family-primary' || name === 'font-family-secondary') return;
      tokensExperimental[name] = value;
    });
    return {
      currentTheme: {
        ...themeContext.currentTheme,
        tokensExperimental: {
          ...themeContext.currentTheme.tokensExperimental,
          ...tokensExperimental
        },
        tokens: {
          ...themeContext.currentTheme.tokens,
          ...tokensExperimental
        },
        colors: {
          ...themeContext.currentTheme.colors,
          ...new_dt,
          colorTextDefault: (isHeader ? dt.overrides.table.headerLabelColor : dt.overrides.table.textColor) ?? new_dt.colorTextDefault
        }
      },
      inverseTheme: themeContext.inverseTheme
    };
  }, [themeContext, isHeader, dt, productTokens]);
  return ignoreTableOverrideTheme ? themeContext : overrideTheme;
}

/**
 * Overrides high level color variables that Table needs.
 *
 * @param dt design token object
 */
const getTableOverrideTheme = (dt, fg) => {
  return {
    baseThemeName: dt.themeVariant,
    colors: {
      ...dt.c,
      colorNeutral: !!fg ? fg : dt.tokens['color-neutral'],
      colorStatusNeutral: !!fg ? fg : dt.tokens['color-neutral']
    },
    tokensExperimental: dt.tokensExperimental,
    tokens: dt.tokens,
    overrides: {}
  };
};
//# sourceMappingURL=useTableOverrideTheme.js.map